/**
 * The /v1 client. Types come from @sable/api-contract, so the app and the
 * API agree on every shape. Workspace-scoped calls carry the workspace id in
 * the x-sable-workspace header; the server checks membership on each one.
 */
import type { ApiErrorBody } from "@sable/api-contract";
import { API_BASE, idToken } from "./session.ts";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly detail?: unknown) {
    super(message);
  }
}

export class SignedOutError extends ApiError {
  constructor() {
    super(401, "unauthenticated", "Sign in to continue");
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  workspace?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
}

async function send(path: string, opts: RequestOptions, token: string): Promise<Response> {
  const qs = opts.query
    ? new URLSearchParams(
        Object.entries(opts.query).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => [k, String(v)]),
      ).toString()
    : "";
  return fetch(`${API_BASE}/v1${path}${qs ? `?${qs}` : ""}`, {
    method: opts.method ?? "GET",
    headers: {
      authorization: `Bearer ${token}`,
      ...(opts.body !== undefined ? { "content-type": "application/json" } : {}),
      ...(opts.workspace ? { "x-sable-workspace": opts.workspace } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let token = await idToken();
  if (!token) throw new SignedOutError();
  let res = await send(path, opts, token);
  if (res.status === 401) {
    token = await idToken(true);
    if (!token) throw new SignedOutError();
    res = await send(path, opts, token);
    if (res.status === 401) throw new SignedOutError();
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = (json ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, err.error ?? "error", err.message ?? `Request failed (${res.status})`, err.detail);
  }
  return json as T;
}
